import { useCheckCsSchedule } from './graphql/useCheckCsSchedule.graphql';
import { openZDChat } from './useZendeskChat';
import { isCsAvailable } from '@orthly/date-fns-business-days';
import { useFeatureFlag } from '@orthly/veneer';
import { atom, useAtom } from 'jotai';
import React from 'react';

const isChatLoadedAtom = atom(false);
const isChatOpenAtom = atom(false);
export const isOutOfOfficeOpenAtom = atom(false);
const unreadChatCountAtom = atom(0);

export const useChat = () => {
    const [isChatLoaded, setIsChatLoaded] = useAtom(isChatLoadedAtom);
    const [isChatOpen, setIsChatOpen] = useAtom(isChatOpenAtom);
    const [isOutOfOfficeOpen, setIsOutOfOfficeOpen] = useAtom(isOutOfOfficeOpenAtom);
    const [unreadChatCount, setUnreadChatCount] = useAtom(unreadChatCountAtom);
    const { value: chatAvailability } = useFeatureFlag('chatAvailability');
    const { value: enableZdScheduler } = useFeatureFlag('enableZdScheduler');
    const { checkSchedule } = useCheckCsSchedule();

    const isAvailable = React.useCallback(async () => {
        if (chatAvailability === 'online') {
            return true;
        }

        let isAvail = false;
        // if we short circuit to offline we still need to know the hours of operation
        // so we have to check the schedule, and hit the zendesk API
        // checkCsSchedule adds the schedule to Jotai which we then access from the unavailable blocker
        if (enableZdScheduler) {
            isAvail = (await checkSchedule()) ?? false;
        }
        // Even turning the flag to offline shouldn't stop an
        // in-progress chat
        if (chatAvailability === 'offline' && unreadChatCount === 0) {
            return false;
        }

        if (enableZdScheduler) {
            return isAvail;
        }

        return isCsAvailable();
    }, [chatAvailability, checkSchedule, enableZdScheduler, unreadChatCount]);

    const openChat = React.useCallback(async () => {
        if (await isAvailable()) {
            setIsOutOfOfficeOpen(false);
            void openZDChat();
            setIsChatOpen(true);
        } else if (unreadChatCount === 0) {
            setIsOutOfOfficeOpen(true);
        }
    }, [isAvailable, setIsChatOpen, setIsOutOfOfficeOpen, unreadChatCount]);

    const closeOutOfOffice = React.useCallback(() => {
        setIsOutOfOfficeOpen(false);
    }, [setIsOutOfOfficeOpen]);

    const setChatLoaded = React.useCallback(() => {
        setIsChatLoaded(true);
    }, [setIsChatLoaded]);

    return {
        closeOutOfOffice,
        isAvailable,
        isChatLoaded,
        isChatOpen,
        isOutOfOfficeOpen,
        openChat,
        setChatLoaded,
        setIsChatOpen,
        setUnreadChatCount,
        unreadChatCount,
    };
};
